import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import {
  LoginResponseBodyType,
  LoginResponseBodyUserType,
} from "@intelligems/ig-types/functions/user-auths/login";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { shopifyGqlClient } from "../../providers/gqlProvider";
import { EXPERIMENT_ROUTES } from "../../routes/routes";
import { clearSnoozeShopifyScopesDialog } from "../../store/slices/appSettings";
import { clearDashboardFilters } from "../../store/slices/dashboardFilters";
import { clearGlobal } from "../../store/slices/global";
import { clearSitewideDashboardFilters } from "../../store/slices/sitewideDashboardFilters";
import { IgStorage } from "../../utils/igStorage";
import { api } from "../api";
import {
  currentAppPath,
  leaveForLoginExit,
} from "../user-auths/leave-for-login-exit";
import { organizationsKeys, userKeys } from "../queryKeys";

export const switchOrganizationByExperience = async (
  token: string,
  experienceId: string
): Promise<LoginResponseBodyType> => {
  const resp = await api.post<LoginResponseBodyType>("/v2/login", {
    token,
    experienceId,
    returnUrl: currentAppPath(),
  });
  return resp.data;
};

export const getPostExperienceSwitchAction = (
  requestedExperience: LoginResponseBodyUserType["requestedExperience"]
): "openExperiments" | "reloadCurrentRoute" =>
  requestedExperience?.isDeleted ? "openExperiments" : "reloadCurrentRoute";

export const useSwitchOrganizationByExperience = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  return useMutation<LoginResponseBodyType, Error, any, any>({
    mutationKey: [{ scope: "user" }],
    mutationFn: async (body: { token: string; experienceId: string }) =>
      switchOrganizationByExperience(body.token, body.experienceId),
    onSuccess: async (data) => {
      if (data.type !== "login") {
        return leaveForLoginExit(data);
      }
      // eslint-disable-next-line react-compiler/react-compiler
      IgStorage.authToken = data.token;

      if (
        getPostExperienceSwitchAction(data.requestedExperience) ===
        "reloadCurrentRoute"
      ) {
        window.location.reload();
        return;
      }

      dispatch(clearGlobal());
      await shopifyGqlClient.resetStore();
      dispatch(clearDashboardFilters());
      dispatch(clearSitewideDashboardFilters());
      dispatch(clearSnoozeShopifyScopesDialog());
      queryClient.clear();
      queryClient.setQueryData(userKeys.all, data);
      queryClient.setQueryData(organizationsKeys.one, {
        org: data.organization,
      });

      void navigate(EXPERIMENT_ROUTES.Experiments, { replace: true });
    },
    onError: () => {
      void navigate(EXPERIMENT_ROUTES.Experiments);
    },
  });
};
